import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Clock, Heart, TrendingUp, Sparkles, Trash2, Play } from 'lucide-react';
import { AUDIO_COLORS, LANGUAGE_FLAGS } from '@/lib/constants';
import { AudioVisualBadge } from '@/components/audio/AudioVisuals';
import { getAudioFileUrl } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';

const HistorialPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [tab, setTab] = useState('recientes'); // recientes, favoritos, tendencias
  const [historial, setHistorial] = useState([]);
  const [favoritos, setFavoritos] = useState([]);

  useEffect(() => {
    // Cargar historial del localStorage
    const savedHistory = localStorage.getItem('audioyou_historial');
    const savedFavorites = localStorage.getItem('audioyou_favoritos');
    
    if (savedHistory) setHistorial(JSON.parse(savedHistory));
    if (savedFavorites) setFavoritos(JSON.parse(savedFavorites));
  }, []);

  const clearHistorial = () => {
    if (window.confirm('¿Borrar todo el historial?')) {
      setHistorial([]);
      localStorage.removeItem('audioyou_historial');
    }
  };

  const toggleFavorito = (audio) => {
    const exists = favoritos.find(f => f.audio_id === audio.audio_id);
    let newFavoritos;
    if (exists) {
      newFavoritos = favoritos.filter(f => f.audio_id !== audio.audio_id);
    } else {
      newFavoritos = [{ ...audio, savedAt: new Date().toISOString() }, ...favoritos];
    }
    setFavoritos(newFavoritos);
    localStorage.setItem('audioyou_favoritos', JSON.stringify(newFavoritos));
  };

  const isFavorito = (audioId) => favoritos.some(f => f.audio_id === audioId);

  // Calcular tendencias (los más reproducidos del historial)
  const getTendencias = () => {
    const counts = {};
    historial.forEach(h => {
      counts[h.audio_id] = (counts[h.audio_id] || 0) + 1;
    });
    
    const uniqueAudios = historial.filter((h, i, arr) => 
      arr.findIndex(x => x.audio_id === h.audio_id) === i
    );
    
    return uniqueAudios
      .map(a => ({ ...a, playCount: counts[a.audio_id] }))
      .sort((a, b) => b.playCount - a.playCount)
      .slice(0, 20);
  };

  const renderAudioList = (audios, showPlayCount = false) => (
    <div className="space-y-2">
      {audios.length === 0 ? (
        <div className="bg-white/10 rounded-2xl p-8 text-center">
          <p className="text-white/50">No hay audios aún</p>
        </div>
      ) : (
        audios.map((audio, idx) => {
          const colorData = AUDIO_COLORS[audio.color] || AUDIO_COLORS.blue;
          const langFlag = LANGUAGE_FLAGS[audio.language]?.flag || '🇪🇸';
          
          return (
            <div
              key={`${audio.audio_id}-${idx}`}
              className="bg-white/5 hover:bg-white/10 rounded-xl p-3 flex items-center gap-3 transition-colors"
            >
              {/* Badge: forma del tipo de audio + color de temática */}
              <AudioVisualBadge
                shape={audio.shape}
                color={audio.color}
                emoji={audio.emoji || colorData.emoji}
                language={audio.language}
                size="sm"
              />
              
              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-white font-medium text-sm">{colorData.name}</p>
                  <span className="text-base">{langFlag}</span>
                  {showPlayCount && audio.playCount > 1 && (
                    <span className="bg-purple-500/30 text-purple-300 text-xs px-2 py-0.5 rounded-full">
                      {audio.playCount}x
                    </span>
                  )}
                </div>
                <p className="text-white/50 text-xs">
                  @{audio.username?.split(' ')[0]} • {audio.duration_seconds}s
                </p>
                {audio.listenedAt && (
                  <p className="text-white/30 text-xs mt-0.5">
                    {new Date(audio.listenedAt).toLocaleDateString('es-ES', { 
                      day: 'numeric', 
                      month: 'short',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                )}
              </div>
              
              {/* Acciones */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  toggleFavorito(audio);
                }}
                className="p-2"
              >
                <Heart 
                  size={20} 
                  className={isFavorito(audio.audio_id) ? 'fill-red-500 text-red-500' : 'text-white/40'}
                />
              </button>
              
              <Link 
                to={`/audio/${audio.audio_id}`}
                className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center"
              >
                <Play size={18} className="text-white ml-0.5" />
              </Link>
            </div>
          );
        })
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/5 border-b border-white/10 p-4">
        <div className="max-w-lg mx-auto flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="text-white/70">
            <ArrowLeft size={24} />
          </button>
          <h1 className="text-xl font-bold text-white flex-1">Mi Historial</h1>
          {tab === 'recientes' && historial.length > 0 && (
            <button onClick={clearHistorial} className="text-red-400 p-2">
              <Trash2 size={20} />
            </button>
          )}
        </div>
      </header>

      <main className="max-w-lg mx-auto px-4 py-6">
        {/* Tabs */}
        <div className="bg-white/10 backdrop-blur-xl rounded-2xl p-1.5 mb-6 flex gap-1">
          <button
            onClick={() => setTab('recientes')}
            className={`flex-1 py-2.5 px-3 rounded-xl flex items-center justify-center gap-2 transition-all text-sm font-medium ${
              tab === 'recientes' ? 'bg-purple-500 text-white' : 'text-white/60'
            }`}
          >
            <Clock size={18} />
            Recientes
          </button>
          <button
            onClick={() => setTab('favoritos')}
            className={`flex-1 py-2.5 px-3 rounded-xl flex items-center justify-center gap-2 transition-all text-sm font-medium ${
              tab === 'favoritos' ? 'bg-purple-500 text-white' : 'text-white/60'
            }`}
          >
            <Heart size={18} />
            Favoritos
          </button>
          <button
            onClick={() => setTab('tendencias')}
            className={`flex-1 py-2.5 px-3 rounded-xl flex items-center justify-center gap-2 transition-all text-sm font-medium ${
              tab === 'tendencias' ? 'bg-purple-500 text-white' : 'text-white/60'
            }`}
          >
            <TrendingUp size={18} />
            Top
          </button>
        </div>

        {/* Estadísticas */}
        <div className="grid grid-cols-3 gap-3 mb-6">
          <div className="bg-white/5 rounded-xl p-3 text-center">
            <p className="text-2xl font-bold text-white">{historial.length}</p>
            <p className="text-white/50 text-xs">Escuchados</p>
          </div>
          <div className="bg-white/5 rounded-xl p-3 text-center">
            <p className="text-2xl font-bold text-white">{favoritos.length}</p>
            <p className="text-white/50 text-xs">Favoritos</p>
          </div>
          <div className="bg-white/5 rounded-xl p-3 text-center">
            <p className="text-2xl font-bold text-white">
              {Math.round(historial.reduce((acc, h) => acc + (h.duration_seconds || 0), 0) / 60)}
            </p>
            <p className="text-white/50 text-xs">Min totales</p>
          </div>
        </div>

        {/* Contenido */}
        {tab === 'recientes' && renderAudioList(historial.slice(0, 50))}
        {tab === 'favoritos' && renderAudioList(favoritos)}
        {tab === 'tendencias' && renderAudioList(getTendencias(), true)}
      </main>
    </div>
  );
};

export default HistorialPage;

// Función helper para guardar en historial (exportar para usar en otros componentes)
export const addToHistorial = (audio) => {
  const saved = localStorage.getItem('audioyou_historial');
  const historial = saved ? JSON.parse(saved) : [];
  
  const newEntry = {
    ...audio,
    listenedAt: new Date().toISOString()
  };
  
  // Añadir al principio, máximo 200 entradas
  const updated = [newEntry, ...historial].slice(0, 200);
  localStorage.setItem('audioyou_historial', JSON.stringify(updated));
};
